import AuthApi from "@tiendeo/auth/dist/AuthApi";
import AuthApiClient from "@tiendeo/auth/dist/AuthApi/AuthApiClient";
import FBAuth from "@tiendeo/auth/dist/FBAuth";
import GoogleAuth from "@tiendeo/auth/dist/GoogleAuth";
import type { Request, Response } from "express";
import type { IConfig } from "config/IConfig";
import type { ProfilePages } from "enums/ProfilePageTypes";
import type { ITexts } from "translations/ITexts";
import type { IServices } from "./IServices";
import BalanceService from "./balanceService";
import CurrencyService from "./currencyService";
import DateService from "./dateService";
import WebviewService from "./webviewService";
import I18NService from "./i18nService";
import UrlsService from "./urlsService";
import NavigationService from "./navigationService";
import { createStatsClient } from "./tiendeoStats";
import { getAuthProps } from "./tiendeoAuth";
import createCookiesServices from "./tiendeoCookies";
import GqlService from "@tiendeo/graphql-client";
import { InternalApiClient } from "@tiendeo/tiendeoapi-client";
import Logger from "./logger";
import GamificationApiClient from "./GamificationApi/gamificationApiClient";

export default function createServices(
    configs: IConfig,
    texts: ITexts,
    pageType: ProfilePages,
    req?: Request,
    res?: Response,
    searchWord?: string,
): IServices {
    const [cookiesClient, _] = createCookiesServices(configs, texts, req, res, searchWord);

    const tiendeoStatsClient = createStatsClient(configs, pageType, cookiesClient, searchWord);

    const authProps = getAuthProps(configs, texts, cookiesClient, tiendeoStatsClient, searchWord);

    const urlsService = new UrlsService(configs);

    return {
        AuthApi: new AuthApi(configs.url_auth_api),
        AuthApiClient: new AuthApiClient({
            countryCode: configs.country_code,
            apiUrl: configs.url_auth_api,
            getCookie: cookiesClient.Get,
        }),
        BalanceService: new BalanceService(configs),
        Configs: configs,
        CookiesClient: cookiesClient,
        CurrencyService: new CurrencyService(
            configs.currency_locale,
            configs.currency_code,
            configs.currency,
        ),
        DateService: new DateService(configs.currency_locale),
        FBAuth: new FBAuth({
            ...authProps,
            loginFacebookApiKey: configs.api_login_facebook,
        }),
        GoogleAuth: new GoogleAuth({
            ...authProps,
            loginGoogleClientId: configs.login_google_client_id,
        }),
        I18NService: new I18NService(
            texts,
            cookiesClient.Get("SHOW_TRANSLATION_KEYS") === "enabled",
        ),
        NavigationService: new NavigationService(urlsService),
        TiendeoStatsClient: tiendeoStatsClient,
        UrlsService: urlsService,
        WebviewService: new WebviewService(cookiesClient, configs),
        GqlService: new GqlService(
            typeof window === "undefined" ? configs.gql_endpoint_internal : configs.gql_endpoint,
            new InternalApiClient({ apiUrl: `${configs.url_tiendeo}/_internalapi/v1` }),
            cookiesClient.Get("usu_tiendeo"),
        ),
        Logger: new Logger(),
        GamificationApiClient: new GamificationApiClient({
            countryCode: configs.country_code,
            getCookie: cookiesClient.Get,
            url: configs.gamification_api_url,
        }),
    };
}
